// C:\IntegraNube_WebStore\src\pages\Categorias.js

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  Chip,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import {
  ArrowForwardOutlined as ArrowForwardIcon,
  CategoryOutlined as CategoryIcon,
  HomeOutlined as HomeIcon,
  Inventory2Outlined as InventoryIcon,
  RefreshOutlined as RefreshIcon,
  StorefrontOutlined as StorefrontIcon,
} from '@mui/icons-material';

import {
  alpha,
} from '@mui/material/styles';

import {
  Link as RouterLink,
} from 'react-router-dom';

import api from '../api/api';

/* ===================================================================== */
/*  Helpers configuración                                                */
/* ===================================================================== */

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object' &&
    !Array.isArray(source.data)
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    !Array.isArray(source.data) &&
    (
      source.data.branding ||
      source.data.nombre
    )
  ) {
    return source.data;
  }

  return source;
}

function safeColor(value, fallback) {
  const color = String(value || '').trim();
  return color || fallback;
}

function cleanString(value) {
  const result = String(value ?? '').trim();
  return result || '';
}

function formatNumber(value, locale = 'es-CL') {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  try {
    return new Intl.NumberFormat(locale).format(number);
  } catch (_) {
    return Math.round(number).toLocaleString('es-CL');
  }
}

/* ===================================================================== */
/*  Helpers categorías                                                   */
/* ===================================================================== */

function extractCategories(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.categorias)) {
    return payload.categorias;
  }

  if (Array.isArray(payload?.data?.categorias)) {
    return payload.data.categorias;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.data?.items)) {
    return payload.data.items;
  }

  return [];
}

function getCategoryPath(categoria) {
  const ruta = cleanString(
    categoria?.ruta
  );

  if (
    ruta &&
    ruta.startsWith('/')
  ) {
    return ruta;
  }

  const slug = cleanString(
    categoria?.slug
  );

  return slug
    ? `/categoria/${encodeURIComponent(slug)}`
    : '/categorias';
}

function getCategoryBanner(categoria) {
  return cleanString(
    categoria?.banner ||
    categoria?.banner_url ||
    categoria?.bannerUrl ||
    categoria?.imagen ||
    categoria?.imagen_url ||
    categoria?.imagenUrl
  );
}

function getCategoryDescription(categoria) {
  return cleanString(
    categoria?.descripcion ||
    categoria?.descripcion_ecommerce ||
    categoria?.descripcionEcommerce
  );
}

function normalizeCategories(items) {
  return (Array.isArray(items) ? items : [])
    .filter((categoria) => {
      const slug = cleanString(
        categoria?.slug
      );

      const ruta = cleanString(
        categoria?.ruta
      );

      const nombre = cleanString(
        categoria?.nombre
      );

      return Boolean(
        nombre &&
        (slug || ruta)
      );
    })
    .sort((a, b) => {
      const ordenA = Number(
        a?.orden ??
        a?.orden_ecommerce ??
        0
      );

      const ordenB = Number(
        b?.orden ??
        b?.orden_ecommerce ??
        0
      );

      if (ordenA !== ordenB) {
        return ordenA - ordenB;
      }

      const productosA = Number(
        a?.productos || 0
      );

      const productosB = Number(
        b?.productos || 0
      );

      if (productosA !== productosB) {
        return productosB - productosA;
      }

      return cleanString(a?.nombre)
        .localeCompare(
          cleanString(b?.nombre),
          'es',
          { sensitivity: 'base' }
        );
    });
}

/* ===================================================================== */
/*  Card categoría                                                       */
/* ===================================================================== */

function CategoryCard({
  categoria,
  colorPrimario,
  colorSecundario,
  colorSuperficie,
  colorTexto,
  colorTextoSecundario,
  locale,
}) {
  const nombre =
    cleanString(categoria?.nombre) ||
    'Categoría';

  const descripcion =
    getCategoryDescription(categoria);

  const banner =
    getCategoryBanner(categoria);

  const productos = Number(
    categoria?.productos || 0
  );

  const [bannerError, setBannerError] =
    useState(false);

  useEffect(() => {
    setBannerError(false);
  }, [banner]);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        bgcolor: colorSuperficie,
        transition:
          'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha(
            colorPrimario,
            0.35
          ),
          boxShadow:
            '0 22px 50px rgba(15,23,42,0.10)',
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={getCategoryPath(categoria)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            minHeight: {
              xs: 176,
              sm: 190,
              md: 205,
            },
            overflow: 'hidden',
            background: `
              radial-gradient(circle at 18% 20%, ${alpha(
                colorPrimario,
                0.24
              )} 0, transparent 36%),
              radial-gradient(circle at 90% 0%, ${alpha(
                colorSecundario,
                0.14
              )} 0, transparent 34%),
              linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)
            `,
          }}
        >
          {banner && !bannerError ? (
            <Box
              component="img"
              src={banner}
              alt={nombre}
              loading="lazy"
              decoding="async"
              onError={() =>
                setBannerError(true)
              }
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 86,
                  height: 86,
                  borderRadius: '28px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(
                    colorSuperficie,
                    0.88
                  ),
                  color: colorPrimario,
                  border: `1px solid ${alpha(
                    colorPrimario,
                    0.16
                  )}`,
                  boxShadow:
                    '0 16px 34px rgba(15,23,42,0.08)',
                }}
              >
                <CategoryIcon
                  sx={{
                    fontSize: 42,
                  }}
                />
              </Box>
            </Box>
          )}

          {banner && !bannerError ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(15,23,42,0.03) 20%, rgba(15,23,42,0.72) 100%)',
              }}
            />
          ) : null}

          {productos > 0 ? (
            <Chip
              icon={
                <InventoryIcon
                  sx={{
                    fontSize:
                      '15px !important',
                  }}
                />
              }
              label={`${formatNumber(
                productos,
                locale
              )} producto${
                productos === 1 ? '' : 's'
              }`}
              size="small"
              sx={{
                position: 'absolute',
                top: 14,
                right: 14,
                bgcolor:
                  'rgba(255,255,255,0.94)',
                color: '#334155',
                fontWeight: 800,
                boxShadow:
                  '0 5px 18px rgba(15,23,42,0.08)',
                '& .MuiChip-icon': {
                  color: colorPrimario,
                },
              }}
            />
          ) : null}

          {banner && !bannerError ? (
            <Typography
              sx={{
                position: 'absolute',
                left: 20,
                right: 20,
                bottom: 18,
                color: '#fff',
                fontSize: {
                  xs: 21,
                  md: 24,
                },
                fontWeight: 950,
                lineHeight: 1.14,
                letterSpacing: '-0.025em',
                textShadow:
                  '0 2px 12px rgba(0,0,0,0.25)',
              }}
            >
              {nombre}
            </Typography>
          ) : null}
        </Box>

        <Stack
          spacing={1.25}
          sx={{
            flex: 1,
            p: {
              xs: 2.1,
              md: 2.4,
            },
          }}
        >
          {!banner || bannerError ? (
            <Typography
              sx={{
                color: colorTexto,
                fontSize: {
                  xs: 19,
                  md: 21,
                },
                fontWeight: 900,
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}
            >
              {nombre}
            </Typography>
          ) : null}

          {descripcion ? (
            <Typography
              sx={{
                color:
                  colorTextoSecundario,
                lineHeight: 1.62,
                fontSize: 14.5,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {descripcion}
            </Typography>
          ) : (
            <Typography
              sx={{
                color:
                  colorTextoSecundario,
                lineHeight: 1.62,
                fontSize: 14.5,
              }}
            >
              Explora los productos disponibles en esta categoría.
            </Typography>
          )}

          <Box sx={{ flex: 1 }} />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{
              pt: 0.4,
            }}
          >
            <Typography
              sx={{
                color: colorPrimario,
                fontWeight: 900,
                fontSize: 14,
              }}
            >
              Ver categoría
            </Typography>

            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(
                  colorPrimario,
                  0.09
                ),
                color: colorPrimario,
                transition:
                  'transform 160ms ease',
                '.MuiCardActionArea-root:hover &': {
                  transform:
                    'translateX(3px)',
                },
              }}
            >
              <ArrowForwardIcon
                sx={{ fontSize: 18 }}
              />
            </Box>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

/* ===================================================================== */
/*  Skeleton                                                             */
/* ===================================================================== */

function CategoriesSkeleton({
  colorSuperficie,
}) {
  return (
    <Grid container spacing={2.5}>
      {[0, 1, 2, 3, 4, 5].map(
        (item) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={item}
          >
            <Box
              sx={{
                bgcolor: colorSuperficie,
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <Skeleton
                variant="rectangular"
                height={200}
              />

              <Box sx={{ p: 2.4 }}>
                <Skeleton
                  width="62%"
                  height={30}
                />
                <Skeleton
                  width="94%"
                  height={21}
                />
                <Skeleton
                  width="75%"
                  height={21}
                />
                <Skeleton
                  width="35%"
                  height={28}
                  sx={{ mt: 1.5 }}
                />
              </Box>
            </Box>
          </Grid>
        )
      )}
    </Grid>
  );
}

/* ===================================================================== */
/*  Categorias                                                           */
/* ===================================================================== */

export default function Categorias({
  tienda = null,
  config = null,
  storeConfig = null,
}) {
  const source =
    tienda ||
    config ||
    storeConfig ||
    {};

  const data = useMemo(
    () => resolveTiendaData(source),
    [source]
  );

  const nombreTienda =
    cleanString(data?.nombre) ||
    'Tienda Online';

  const colorPrimario = safeColor(
    data?.branding?.colorPrimario,
    '#1976d2'
  );

  const colorSecundario = safeColor(
    data?.branding?.colorSecundario,
    '#111827'
  );

  const colorFondo = safeColor(
    data?.branding?.colorFondo,
    '#f8fafc'
  );

  const colorSuperficie = safeColor(
    data?.branding?.colorSuperficie,
    '#ffffff'
  );

  const colorTexto = safeColor(
    data?.branding?.colorTexto,
    '#111827'
  );

  const colorTextoSecundario = safeColor(
    data?.branding?.colorTextoSecundario,
    '#64748b'
  );

  const locale =
    cleanString(
      data?.regionalizacion?.locale
    ) ||
    'es-CL';

  const [categorias, setCategorias] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const cargarCategorias = async (
    signal = undefined
  ) => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get(
        '/categorias',
        signal
          ? { signal }
          : undefined
      );

      const items = normalizeCategories(
        extractCategories(
          response?.data
        )
      );

      setCategorias(items);
    } catch (requestError) {
      if (
        requestError?.name ===
          'CanceledError' ||
        requestError?.name ===
          'AbortError' ||
        requestError?.code ===
          'ERR_CANCELED'
      ) {
        return;
      }

      console.error(
        '[Categorias] No fue posible cargar categorías:',
        requestError
      );

      setCategorias([]);
      setError(
        requestError?.response?.data?.message ||
        'No fue posible cargar las categorías de la tienda.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller =
      new AbortController();

    cargarCategorias(
      controller.signal
    );

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cantidadCategorias =
    categorias.length;

  return (
    <Box
      sx={{
        minHeight: '70vh',
        bgcolor: colorFondo,
        color: colorTexto,
      }}
    >
      {/* =============================================================== */}
      {/* Hero                                                            */}
      {/* =============================================================== */}

      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderBottom:
            '1px solid #e5e7eb',
          background: `
            radial-gradient(circle at 12% 18%, ${alpha(
              colorPrimario,
              0.18
            )} 0, transparent 30%),
            radial-gradient(circle at 92% 12%, ${alpha(
              colorSecundario,
              0.10
            )} 0, transparent 28%),
            linear-gradient(180deg, ${colorSuperficie} 0%, ${colorFondo} 100%)
          `,
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: {
              xs: 4,
              sm: 5,
              md: 6,
            },
          }}
        >
          <Breadcrumbs
            aria-label="Navegación"
            sx={{
              mb: 2.5,
              '& .MuiBreadcrumbs-separator': {
                color: '#cbd5e1',
              },
            }}
          >
            <Button
              component={RouterLink}
              to="/"
              startIcon={<HomeIcon />}
              sx={{
                minWidth: 0,
                px: 0,
                color:
                  colorTextoSecundario,
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              Inicio
            </Button>

            <Typography
              sx={{
                color: colorTexto,
                fontWeight: 800,
              }}
            >
              Categorías
            </Typography>
          </Breadcrumbs>

          <Grid
            container
            spacing={{ xs: 3, md: 5 }}
            alignItems="center"
          >
            <Grid item xs={12} md={8}>
              <Stack
                spacing={1.7}
                alignItems="flex-start"
              >
                <Chip
                  icon={<CategoryIcon />}
                  label="Explorar catálogo"
                  size="small"
                  sx={{
                    bgcolor: alpha(
                      colorPrimario,
                      0.09
                    ),
                    color: colorPrimario,
                    border: `1px solid ${alpha(
                      colorPrimario,
                      0.16
                    )}`,
                    fontWeight: 900,
                    '& .MuiChip-icon': {
                      color: colorPrimario,
                    },
                  }}
                />

                <Typography
                  component="h1"
                  sx={{
                    fontSize: {
                      xs: 34,
                      sm: 43,
                      md: 50,
                    },
                    lineHeight: 1.04,
                    letterSpacing:
                      '-0.045em',
                    fontWeight: 950,
                    color: colorTexto,
                  }}
                >
                  Categorías de productos
                </Typography>

                <Typography
                  sx={{
                    maxWidth: 720,
                    color:
                      colorTextoSecundario,
                    fontSize: {
                      xs: 16,
                      md: 18,
                    },
                    lineHeight: 1.7,
                  }}
                >
                  Encuentre más rápido lo que busca explorando el catálogo de {nombreTienda} por categoría.
                </Typography>

                {!loading &&
                !error &&
                cantidadCategorias > 0 ? (
                  <Typography
                    sx={{
                      color: colorPrimario,
                      fontWeight: 850,
                      fontSize: 14.5,
                    }}
                  >
                    {cantidadCategorias}{' '}
                    categoría
                    {cantidadCategorias === 1
                      ? ''
                      : 's'}{' '}
                    disponible
                    {cantidadCategorias === 1
                      ? ''
                      : 's'}
                  </Typography>
                ) : null}
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: {
                    xs: 'none',
                    md: 'grid',
                  },
                  placeItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 190,
                    height: 190,
                    position: 'relative',
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '52px',
                    bgcolor: alpha(
                      colorSuperficie,
                      0.82
                    ),
                    border: `1px solid ${alpha(
                      colorPrimario,
                      0.12
                    )}`,
                    boxShadow:
                      '0 30px 70px rgba(15,23,42,0.10)',
                    transform:
                      'rotate(4deg)',
                  }}
                >
                  <StorefrontIcon
                    sx={{
                      fontSize: 84,
                      color: colorPrimario,
                      transform:
                        'rotate(-4deg)',
                    }}
                  />

                  <Box
                    sx={{
                      position: 'absolute',
                      width: 54,
                      height: 54,
                      borderRadius: '50%',
                      right: -12,
                      bottom: 18,
                      bgcolor: colorPrimario,
                      color: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      boxShadow:
                        '0 14px 30px rgba(15,23,42,0.15)',
                    }}
                  >
                    <CategoryIcon />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* =============================================================== */}
      {/* Contenido                                                        */}
      {/* =============================================================== */}

      <Container
        maxWidth="xl"
        sx={{
          py: {
            xs: 4,
            md: 6,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={2}
          alignItems={{
            xs: 'flex-start',
            sm: 'center',
          }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              component="h2"
              sx={{
                color: colorTexto,
                fontSize: {
                  xs: 25,
                  md: 31,
                },
                fontWeight: 900,
                letterSpacing: '-0.035em',
              }}
            >
              Explore por categoría
            </Typography>

            <Typography
              sx={{
                mt: 0.6,
                color:
                  colorTextoSecundario,
                lineHeight: 1.6,
              }}
            >
              Seleccione una categoría para ver sus productos, ofertas y filtros disponibles.
            </Typography>
          </Box>

          <Button
            component={RouterLink}
            to="/productos"
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            sx={{
              minHeight: 44,
              px: 2.2,
              borderRadius: 2.2,
              borderColor: alpha(
                colorPrimario,
                0.34
              ),
              color: colorPrimario,
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: colorSuperficie,
              '&:hover': {
                borderColor:
                  colorPrimario,
                bgcolor: alpha(
                  colorPrimario,
                  0.04
                ),
              },
            }}
          >
            Ver catálogo completo
          </Button>
        </Stack>

        {loading ? (
          <CategoriesSkeleton
            colorSuperficie={
              colorSuperficie
            }
          />
        ) : error ? (
          <Box
            sx={{
              maxWidth: 760,
              mx: 'auto',
              py: 3,
            }}
          >
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() =>
                    cargarCategorias()
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  Reintentar
                </Button>
              }
              sx={{
                borderRadius: 3,
                alignItems: 'center',
              }}
            >
              {error}
            </Alert>
          </Box>
        ) : categorias.length > 0 ? (
          <Grid container spacing={2.5}>
            {categorias.map(
              (categoria) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={
                    categoria?.id ??
                    categoria?.slug ??
                    categoria?.ruta
                  }
                >
                  <CategoryCard
                    categoria={categoria}
                    colorPrimario={
                      colorPrimario
                    }
                    colorSecundario={
                      colorSecundario
                    }
                    colorSuperficie={
                      colorSuperficie
                    }
                    colorTexto={colorTexto}
                    colorTextoSecundario={
                      colorTextoSecundario
                    }
                    locale={locale}
                  />
                </Grid>
              )
            )}
          </Grid>
        ) : (
          <Box
            sx={{
              maxWidth: 680,
              mx: 'auto',
              py: {
                xs: 6,
                md: 9,
              },
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 82,
                height: 82,
                mx: 'auto',
                mb: 2,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 4,
                bgcolor: alpha(
                  colorPrimario,
                  0.08
                ),
                color: colorPrimario,
              }}
            >
              <CategoryIcon
                sx={{ fontSize: 42 }}
              />
            </Box>

            <Typography
              sx={{
                color: colorTexto,
                fontSize: 24,
                fontWeight: 900,
              }}
            >
              Aún no hay categorías disponibles
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color:
                  colorTextoSecundario,
                lineHeight: 1.65,
              }}
            >
              Puede revisar el catálogo completo mientras se organizan nuevas categorías para la tienda.
            </Typography>

            <Button
              component={RouterLink}
              to="/productos"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                mt: 2.5,
                bgcolor: colorPrimario,
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: 2.2,
                '&:hover': {
                  bgcolor: colorPrimario,
                  filter:
                    'brightness(0.92)',
                },
              }}
            >
              Ver productos
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
